import { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { submitContactForm, submitRegistrationForm } from '../mock';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';

const Contact = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [registrationForm, setRegistrationForm] = useState({
    familyName: '',
    email: '',
    phone: '',
    address: '',
    familyMembers: '',
    duration: '',
    expectations: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const result = await submitContactForm(contactForm);
      toast.success(t.contact.successMessage);
      setContactForm({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      toast.error(t.contact.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegistrationSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const result = await submitRegistrationForm(registrationForm);
      toast.success(t.contact.registrationSuccess);
      setRegistrationForm({
        familyName: '',
        email: '',
        phone: '',
        address: '',
        familyMembers: '',
        duration: '',
        expectations: ''
      });
    } catch (error) {
      toast.error(t.contact.errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Hero Section */}
      <section className="page-hero">
        <div className="page-hero-content">
          <h1 className="page-title">{t.contact.title}</h1>
          <p className="page-subtitle">
            {t.contact.subtitle}
          </p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="section-container">
        <div className="contact-info-grid">
          <div className="contact-info-card">
            <MapPin size={32} className="contact-icon" />
            <h3>{t.contact.addressTitle}</h3>
            <p>{t.contact.addressText}<br />{t.contact.addressCountry}</p>
          </div>
          <div className="contact-info-card">
            <Mail size={32} className="contact-icon" />
            <h3>{t.contact.emailTitle}</h3>
            <p>contact@tereka.fr</p>
          </div>
          <div className="contact-info-card">
            <Phone size={32} className="contact-icon" />
            <h3>{t.contact.phoneTitle}</h3>
            <p>+33 X XX XX XX XX</p>
          </div>
        </div>
      </section>

      {/* Forms Section */}
      <section className="forms-section">
        <div className="section-container">
          <Tabs defaultValue="contact" className="forms-tabs">
            <TabsList className="tabs-list">
              <TabsTrigger value="contact">{t.contact.simpleContact}</TabsTrigger>
              <TabsTrigger value="registration">{t.contact.registration}</TabsTrigger>
            </TabsList>

            {/* Contact Form */}
            <TabsContent value="contact">
              <Card className="form-card">
                <CardHeader>
                  <CardTitle>{t.contact.formTitle}</CardTitle>
                  <CardDescription>
                    {t.contact.formSubtitle}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleContactSubmit} className="contact-form">
                    <div className="form-group">
                      <Label htmlFor="name">{t.contact.nameLabel} *</Label>
                      <Input
                        id="name"
                        type="text"
                        required
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        placeholder={t.contact.namePlaceholder}
                      />
                    </div>

                    <div className="form-group">
                      <Label htmlFor="email">{t.contact.emailLabel} *</Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        placeholder={t.contact.emailPlaceholder}
                      />
                    </div>

                    <div className="form-group">
                      <Label htmlFor="phone">{t.contact.phoneLabel}</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        placeholder={t.contact.phonePlaceholder}
                      />
                    </div>

                    <div className="form-group">
                      <Label htmlFor="message">{t.contact.messageLabel} *</Label>
                      <Textarea
                        id="message"
                        required
                        value={contactForm.message}
                        onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                        placeholder={t.contact.messagePlaceholder}
                        rows={5}
                      />
                    </div>

                    <Button type="submit" disabled={isSubmitting} className="submit-btn">
                      {isSubmitting ? t.contact.sending : t.contact.sendButton}
                      <Send size={18} className="ml-2" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Registration Form */}
            <TabsContent value="registration">
              <Card className="form-card">
                <CardHeader>
                  <CardTitle>{t.contact.registrationTitle}</CardTitle>
                  <CardDescription>
                    {t.contact.registrationSubtitle}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegistrationSubmit} className="contact-form">
                    <div className="form-group">
                      <Label htmlFor="familyName">{t.contact.familyNameLabel} *</Label>
                      <Input
                        id="familyName"
                        type="text"
                        required
                        value={registrationForm.familyName}
                        onChange={(e) => setRegistrationForm({ ...registrationForm, familyName: e.target.value })}
                        placeholder={t.contact.familyNamePlaceholder}
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <Label htmlFor="reg-email">{t.contact.emailLabel} *</Label>
                        <Input
                          id="reg-email"
                          type="email"
                          required
                          value={registrationForm.email}
                          onChange={(e) => setRegistrationForm({ ...registrationForm, email: e.target.value })}
                          placeholder={t.contact.emailPlaceholder}
                        />
                      </div>

                      <div className="form-group">
                        <Label htmlFor="reg-phone">{t.contact.phoneLabel} *</Label>
                        <Input
                          id="reg-phone"
                          type="tel"
                          required
                          value={registrationForm.phone}
                          onChange={(e) => setRegistrationForm({ ...registrationForm, phone: e.target.value })}
                          placeholder={t.contact.phonePlaceholder}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <Label htmlFor="address">{t.contact.addressLabel} *</Label>
                      <Input
                        id="address"
                        type="text"
                        required
                        value={registrationForm.address}
                        onChange={(e) => setRegistrationForm({ ...registrationForm, address: e.target.value })}
                        placeholder={t.contact.addressPlaceholder}
                      />
                    </div>

                    <div className="form-row">
                      <div className="form-group">
                        <Label htmlFor="familyMembers">{t.contact.familyMembersLabel} *</Label>
                        <Input
                          id="familyMembers"
                          type="text"
                          required
                          value={registrationForm.familyMembers}
                          onChange={(e) => setRegistrationForm({ ...registrationForm, familyMembers: e.target.value })}
                          placeholder={t.contact.familyMembersPlaceholder}
                        />
                      </div>

                      <div className="form-group">
                        <Label htmlFor="duration">{t.contact.durationLabel} *</Label>
                        <Input
                          id="duration"
                          type="text"
                          required
                          value={registrationForm.duration}
                          onChange={(e) => setRegistrationForm({ ...registrationForm, duration: e.target.value })}
                          placeholder={t.contact.durationPlaceholder}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <Label htmlFor="expectations">{t.contact.expectationsLabel} *</Label>
                      <Textarea
                        id="expectations"
                        required
                        value={registrationForm.expectations}
                        onChange={(e) => setRegistrationForm({ ...registrationForm, expectations: e.target.value })}
                        placeholder={t.contact.expectationsPlaceholder}
                        rows={5}
                      />
                    </div>

                    <Button type="submit" disabled={isSubmitting} className="submit-btn">
                      {isSubmitting ? t.contact.sending : t.contact.registerButton}
                      <Send size={18} className="ml-2" />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default Contact;
